<template>
  <validation-provider
    :rules="validation"
    :name="errorName || label"
    v-slot="{ errors, classes }"
  >
    <div :class="['brb-select', classes]">
      <multiselect
        :id="id"
        v-model="model"
        :allow-empty="allowEmpty"
        :class="[
          'flex-grow-1 text-right',
          {
            'brb-multi-select': multiple,
            'brb-single-select': !multiple
          },
          inputClass
        ]"
        :deselect-label="deselectLabel"
        :disabled="disabled"
        :label="optionLabel"
        :multiple="multiple"
        :options="computedOptions"
        :searchable="searchable"
        :select-label="selectLabel"
        :selected-label="selectedLabel"
        :show-labels="showLabels"
        :track-by="trackBy"
        :placeholder="label || $t('shared.choose')"
        @select="$emit('select', $event)"
      >
        <span slot="noResult" class="text-muted text-center d-block py-1">
          {{ noResultText }}
        </span>

        <span slot="noOptions" class="text-muted text-center d-block py-1">
          {{ noOptionsText }}
        </span>

        <div slot="tag" slot-scope="{ option, remove }" class="barbanet-tag sm">
          <span>{{ option[optionLabel] }}</span>

          <font-awesome-icon
            class="text-danger mr-3 cursor-pointer"
            icon="times"
            @mousedown.prevent="remove(option)"
          />
        </div>

        <span slot="placeholder" :class="['label', { selected: model }]">
          <span class="placeholder font-bold">{{ label }}</span>
          <span v-if="required" class="text-danger"> * </span>
        </span>

        <span slot="singleLabel">
          <span v-if="model">{{ model[optionLabel] }}</span>
          <span
            :class="[
              'label font-bold',
              model ? 'selected' : 'd-none',
              labelClass
            ]"
          >
            {{ label }} <span class="text-danger" v-if="required">*</span>
          </span>
        </span>
      </multiselect>

      <span
        v-if="errors && errors.length"
        class="d-block text-right text-danger small mt-2"
      >
        {{ errors[0] }}
      </span>
    </div>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import Multiselect from 'vue-multiselect'

export default {
  name: 'BrbSelect',
  components: {
    ValidationProvider,
    Multiselect
  },
  props: {
    id: {
      type: String
    },
    value: {
      default: null
    },
    validation: {
      type: [String, Object],
      default: null
    },
    label: String,
    errorName: String,
    labelClass: [String, Object, Array],
    options: {
      type: Array,
      default: () => []
    },
    exclude: {
      type: Array,
      default: () => []
    },
    optionLabel: {
      type: String,
      default: 'title'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    prependTrackByValue: Boolean,
    noOptionsText: {
      type: String,
      default() {
        return this.$t('shared.nothingAdded')
      }
    },
    noResultText: {
      type: String,
      default() {
        return this.$t('shared.nothingFound')
      }
    },
    showLabels: Boolean,
    selectLabel: {
      type: String,
      default() {
        return this.$t('shared.pressEnterToSelect')
      }
    },
    deselectLabel: {
      type: String,
      default() {
        return this.$t('shared.pressEnterToRemove')
      }
    },
    selectedLabel: {
      type: String,
      default() {
        return this.$t('shared.selected')
      }
    },
    multiple: Boolean,
    searchable: Boolean,
    allowEmpty: Boolean,
    prependOption: Boolean,
    prependOptionText: {
      type: String,
      default() {
        return this.$t('shared.choose')
      }
    },
    append: String,
    inputClass: {
      type: [String, Object, Array]
    },
    disabled: Boolean
  },
  computed: {
    model: {
      get() {
        return this.value !== null
          ? this.multiple
            ? Array.isArray(this.value)
              ? this.options.filter((option) =>
                  this.value.includes(option[this.trackBy])
                )
              : this.value
            : this.options.find((option) => option[this.trackBy] === this.value)
          : this.value
      },
      set(value) {
        const output =
          value !== null
            ? this.multiple
              ? value.map((option) => option[this.trackBy])
              : value[this.trackBy]
            : value
        this.$emit('input', output)
      }
    },
    computedOptions() {
      let options = Object.assign([], this.options)
      if (this.prependOption)
        options.unshift({
          [this.trackBy]: null,
          [this.optionLabel]: this.prependOptionText
        })

      if (this.exclude && this.exclude.length)
        options = options.filter(
          (option) => !this.exclude.includes(option[this.trackBy])
        )

      if (this.prependTrackByValue)
        return options.map((option) => {
          return {
            ...option,
            [this.optionLabel]: `${
              option[this.trackBy] !== null ? `${option[this.trackBy]} - ` : ''
            }${option[this.optionLabel]}`
          }
        })
      else return options
    },
    required() {
      if (this.validation) {
        if (typeof this.validation === 'string')
          return this.validation.includes('required')

        if (
          typeof this.validation === 'object' &&
          Object.keys(this.validation).includes('required')
        )
          return this.validation.required
      }

      return false
    }
  }
}
</script>

<style lang="scss">
@import '~/src/assets/scss/components/brb-components/brb-select';
</style>
